import { Link } from "react-router-dom";
import RoosterLogo from "@/components/RoosterLogo";

const COMPANY = "Weathervane, LLC";
const CONTACT_EMAIL = "hello@weathervaneapp.net";
const LAST_UPDATED = "April 2026";

/** Terms of Service. Drafted to satisfy iOS App Store + Google Play
 *  requirements. Review with counsel before public launch. */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-10 lg:py-16">
        <div className="ticker-label mb-4">Last updated · {LAST_UPDATED}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-8" data-testid="terms-title">
          Terms of Service
        </h1>

        <Section title="1. Agreement">
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your use of
            Weathervane, operated by {COMPANY} (&quot;we,&quot; &quot;us&quot;).
            By creating an account or using the service, you agree to these
            Terms.
          </p>
        </Section>

        <Section title="2. The service">
          <p>
            Weathervane provides AI-synthesized weather forecasts and news
            digests delivered by email and push notification. Forecasts are
            assembled from multiple public weather models. News is curated from
            public web sources.
          </p>
          <p className="mt-3">
            Forecasts are <span className="font-semibold">informational only.</span>
            {" "}They are not a substitute for official emergency alerts or
            professional advice. Don&apos;t rely on Weathervane for life-safety
            decisions during severe weather; check NOAA, your national weather
            service, and local authorities.
          </p>
        </Section>

        <Section title="3. Your account">
          <p>
            You must provide accurate information when registering. You&apos;re
            responsible for keeping your password secret and for any activity on
            your account. Tell us right away if you suspect unauthorized access.
          </p>
          <p className="mt-3">
            You must be at least 13 years old to use Weathervane. If you&apos;re
            in the EU, the minimum age may be higher under your local law.
          </p>
        </Section>

        <Section title="4. Acceptable use">
          <ul className="list-disc pl-6 space-y-2">
            <li>Don&apos;t resell, scrape, or republish forecast/news content commercially without our written permission.</li>
            <li>Don&apos;t attempt to break, overload, or reverse-engineer the service.</li>
            <li>Don&apos;t use Weathervane to harass others, send spam, or violate any law.</li>
            <li>Don&apos;t impersonate someone else when creating an account.</li>
          </ul>
        </Section>

        <Section title="5. Subscription cancellation">
          <p>
            You can cancel any subscription or delete your account at any time
            from your dashboard or by emailing{" "}
            <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            Weathervane is currently free; if we introduce paid tiers in the
            future we&apos;ll notify you and provide a clear opt-in.
          </p>
        </Section>

        <Section title="6. Intellectual property">
          <p>
            The Weathervane name, rooster logo, brand assets, AI prompts, code,
            and email templates are our property. The forecast data we ensemble
            comes from third-party weather providers under their respective
            licenses. News article text and links remain the property of the
            originating publishers; we display short summaries with full
            attribution and link out to the source.
          </p>
        </Section>

        <Section title="7. Disclaimer of warranties">
          <p>
            Weathervane is provided &quot;as is.&quot; To the maximum extent
            permitted by law, we disclaim all warranties — express or implied —
            including merchantability, fitness for a particular purpose, and
            non-infringement. We don&apos;t guarantee uninterrupted service,
            specific delivery times, or perfect forecast accuracy.
          </p>
        </Section>

        <Section title="8. Limitation of liability">
          <p>
            To the fullest extent permitted by law, {COMPANY} is not liable for
            any indirect, incidental, special, consequential, or punitive
            damages arising out of your use of Weathervane. Our total liability
            for any claim related to the service is limited to USD $100 or the
            amount you paid us in the prior twelve months — whichever is greater.
          </p>
        </Section>

        <Section title="9. Indemnification">
          <p>
            You agree to indemnify and hold {COMPANY} harmless from any claim
            or demand — including reasonable attorneys&apos; fees — made by any
            third party arising from your violation of these Terms or your
            misuse of the service.
          </p>
        </Section>

        <Section title="10. Termination">
          <p>
            We may suspend or terminate your account if you violate these
            Terms or if continuing to provide the service to you would expose
            us to legal risk. You can terminate your account anytime; on
            termination we delete your data per our Privacy Policy.
          </p>
        </Section>

        <Section title="11. Changes to these Terms">
          <p>
            We may update these Terms occasionally. Material changes will be
            sent to your email and posted here. Continued use after a change
            means you accept the updated Terms.
          </p>
        </Section>

        <Section title="12. Governing law">
          <p>
            These Terms are governed by the laws of the State of Delaware,
            United States, without regard to conflict-of-law principles.
            Disputes will be resolved in the state or federal courts located in
            Delaware.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Email <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-black/10 text-sm text-neutral-700">
          <Link to="/" className="underline">← Back to Weathervane</Link>
          <span className="mx-3 text-neutral-300">|</span>
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/?stay=1" className="flex items-center gap-3">
          <RoosterLogo size={36} />
          <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
        </Link>
      </div>
    </header>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-9">
      <h2 className="font-display text-2xl font-black tracking-tighter mb-3">{title}</h2>
      <div className="text-[15px] text-neutral-800 leading-relaxed">{children}</div>
    </section>
  );
}
